import React, { useState } from 'react';
import { 
  ChevronDown, Play, Upload, Plus, Save, Sparkles, 
  Component, Languages, Bot, Voicemail, Key, Image, Target, FileText, User, PhoneCall, Mic, PhoneOff
} from 'lucide-react';

const VoiceAgentCreator = () => {
  const [selectedVoice, setSelectedVoice] = useState('Rachel');
  const [agentType, setAgentType] = useState('Pre-screening');
  const [language, setLanguage] = useState('English (US)');
  const [initialMessage, setInitialMessage] = useState("Hello! I'm your AI assistant. How can I help you today?");
  const [objective, setObjective] = useState('');
  const [instructions, setInstructions] = useState('');
  const [specialtyKeywords, setSpecialtyKeywords] = useState([]);
  const [agentName, setAgentName] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [callStatus, setCallStatus] = useState('Idle');

  const voices = [
    { name: 'Rachel', type: 'American female', description: 'Calm and professional' },
    { name: 'Drew', type: 'American male', description: 'Friendly and warm' },
    { name: 'Clyde', type: 'American male', description: 'Middle-aged, confident' },
    { name: 'Paul', type: 'American male', description: 'Mature and authoritative' },
    { name: 'Domi', type: 'American female', description: 'Young and energetic' },
    { name: 'Dave', type: 'British male', description: 'Conversational, relaxed' }
  ];

  const handlePlayVoice = (voiceName) => {
    // Voice preview functionality would go here
    console.log(`Playing ${voiceName} voice preview`);
  };

  const handleAddKeyword = () => {
    // Add keyword functionality
    console.log('Add specialty keyword');
  };

  const handleSaveDraft = () => {
    console.log('Saving draft...');
  };

  const handleCreateAgent = () => {
    console.log('Creating voice agent...');
  };

  const handleTestCall = () => {
    // This would involve Web Audio/WebRTC APIs to connect to the agent
    if (!isTesting) {
      console.log('Starting in-browser test call...');
      setCallStatus('Connecting...');
      setIsTesting(true);
      // Simulate connection for demonstration
      setTimeout(() => {
        setCallStatus('Active');
        // In a real app, you would establish a WebSocket or WebRTC connection here
      }, 1500);
    } else {
      console.log('Ending in-browser test call...');
      setCallStatus('Ended');
      setIsTesting(false);
      // Disconnect logic here
      setTimeout(() => setCallStatus('Idle'), 1500);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        <h1 className="text-2xl font-semibold text-gray-900 text-center mb-8">
          Create and customize your AI voice agent
        </h1>
        
        <div className="bg-white rounded-2xl p-8 shadow-sm">
          {/* Grid container for a two-column layout on larger screens */}
          <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-x-12">
            {/* Left Column */}
            <div className="lg:col-span-1">
              {/* Agent Type and Language */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                    <Component className="w-4 h-4 mr-2 text-purple-600" />
                    Agent Type
                  </label>
                  <div className="relative">
                    <select 
                      value={agentType}
                      onChange={(e) => setAgentType(e.target.value)}
                      className="w-full p-3 border border-gray-200 rounded-lg appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option>Pre-screening</option>
                      <option>Customer Support</option>
                      <option>Sales Assistant</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  </div>
                </div>
                
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                    <Languages className="w-4 h-4 mr-2 text-purple-600" />
                    Default Language
                  </label>
                  <div className="relative">
                    <select 
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      className="w-full p-3 border border-gray-200 rounded-lg appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option>English (US)</option>
                      <option>English (UK)</option>
                      <option>Spanish</option>
                      <option>French</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Initial Message */}
              <div className="mb-8">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                  <Bot className="w-4 h-4 mr-2 text-purple-600" />
                  Initial Message
                </label>
                <textarea
                  value={initialMessage}
                  onChange={(e) => setInitialMessage(e.target.value)}
                  className="w-full p-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  rows="3"
                />
              </div>

              {/* Voice Selection */}
              <div className="mb-8">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                  <Voicemail className="w-4 h-4 mr-2 text-purple-600" />
                  Voice Selection
                </label>
                
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search voices..."
                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {voices.map((voice, index) => (
                    <div
                      key={voice.name}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        selectedVoice === voice.name
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedVoice(voice.name)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-gray-900">{voice.name}</h3>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlayVoice(voice.name);
                          }}
                          className="p-1 text-gray-400 hover:text-purple-500 transition-colors"
                        >
                          <Play className="w-5 h-5" />
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">{voice.type}</p>
                      <p className="text-xs text-gray-500">{voice.description}</p>
                    </div>
                  ))}
                </div>

                <p className="text-sm text-purple-600">
                  Selected Voice: <span className="font-medium">{selectedVoice} (American)</span>
                </p>
              </div>
            </div>

            {/* Right Column */}
            <div className="lg:col-span-1">
              {/* Agent Name */}
              <div className="mb-8">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                  <User className="w-4 h-4 mr-2 text-purple-600" />
                  Agent Name
                </label>
                <input
                  type="text"
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  placeholder="Enter a name for your agent..."
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Agent Avatar */}
              <div className="mb-8">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                  <Image className="w-4 h-4 mr-2 text-purple-600" />
                  Agent Avatar
                </label>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Click to upload agent avatar</p>
                  <p className="text-sm text-gray-500">PNG, JPG up to 5MB</p>
                </div>
              </div>

              {/* Objective */}
              <div className="mb-8">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                  <Target className="w-4 h-4 mr-2 text-purple-600" />
                  Objective
                </label>
                <textarea
                  value={objective}
                  onChange={(e) => setObjective(e.target.value)}
                  placeholder="Define the main goal for your agent..."
                  className="w-full p-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  rows="2"
                />
              </div>

              {/* Instructions */}
              <div className="mb-8">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                  <FileText className="w-4 h-4 mr-2 text-purple-600" />
                  Instructions
                </label>
                <textarea
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Provide detailed instructions for the voice agent's behavior and responses..."
                  className="w-full p-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  rows="8"
                />
              </div>
            </div>
          </div>

          {/* Test Agent Section */}
          <div className="pt-8 mt-8 border-t border-gray-100">
            <label className="flex items-center text-lg font-medium text-gray-800 mb-4">
              <PhoneCall className="w-5 h-5 mr-3 text-purple-600" />
              Test Your Agent
            </label>
            <p className="text-sm text-gray-500 mb-4">
              Use your device's microphone and speakers to test the agent in real-time.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <button
                onClick={handleTestCall}
                className={`flex items-center justify-center gap-2 w-full sm:w-auto py-3 px-6 rounded-lg transition-colors font-medium ${
                  isTesting
                    ? 'bg-red-500 hover:bg-red-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                {isTesting ? <PhoneOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                {isTesting ? 'End Test Call' : 'Start Test Call'}
              </button>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Status:</span>
                <span className="font-medium">{callStatus}</span>
                {isTesting && callStatus === 'Connecting...' && (
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                )}
                 {isTesting && callStatus === 'Active' && (
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons (remain full-width) */}
          <div className="flex gap-4 pt-8 mt-8 border-t border-gray-100">
            <button
              onClick={handleSaveDraft}
              className="flex-1 flex items-center justify-center gap-2 py-3 px-6 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              <Save className="w-5 h-5" />
              Save Draft
            </button>
            <button
              onClick={handleCreateAgent}
              className="flex-1 flex items-center justify-center gap-2 py-3 px-6 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
            >
              <Sparkles className="w-5 h-5" />
              Create Voice Agent
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceAgentCreator;